import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { UserPlus, Mail, Lock, User, Phone, MapPin, Eye, EyeOff, ArrowLeft } from "lucide-react";
import logo from "@/assets/logo.png";
import BottomNav from "@/components/BottomNav";

const SignUp = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    password: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const update = (key: string, value: string) => setForm((p) => ({ ...p, [key]: value }));

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password || !form.fullName.trim()) return;
    if (form.password.length < 6) {
      toast({ title: "Password too short", description: "Must be at least 6 characters", variant: "destructive" });
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.auth.signUp({
      email: form.email.trim(),
      password: form.password,
      options: {
        emailRedirectTo: window.location.origin,
        data: { full_name: form.fullName.trim() },
      },
    });
    if (error) {
      setLoading(false);
      toast({ title: "Sign up failed", description: error.message, variant: "destructive" });
      return;
    }

    // Update profile with extra details
    if (data.user) {
      await supabase.from("profiles").update({
        phone: form.phone.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zip: form.zip.trim(),
      }).eq("id", data.user.id);
    }

    setLoading(false);
    toast({ title: "Account created!", description: "Check your email to verify your account." });
    navigate("/signin");
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="max-w-md mx-auto px-5 pt-12 space-y-6">
        <button onClick={() => navigate("/")} className="w-8 h-8 rounded-lg bg-card border border-border flex items-center justify-center hover:border-primary/40 transition-colors">
          <ArrowLeft className="w-4 h-4 text-foreground" />
        </button>

        <div className="text-center space-y-3">
          <img src={logo} alt="Lynx to the Past" className="w-full max-w-sm mx-auto" />
          <h1 className="text-2xl font-mono font-bold text-foreground">Create Account</h1>
          <p className="text-xs font-mono text-muted-foreground">Join us today</p>
        </div>

        <form onSubmit={handleSignUp} className="space-y-4">
          <div className="space-y-2">
            <label className="text-xs font-mono text-muted-foreground">Full Name *</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="John Doe" value={form.fullName} onChange={(e) => update("fullName", e.target.value)} className="pl-10 font-mono text-sm bg-card border-border" required maxLength={100} />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-mono text-muted-foreground">Email *</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input type="email" placeholder="your@email.com" value={form.email} onChange={(e) => update("email", e.target.value)} className="pl-10 font-mono text-sm bg-card border-border" required maxLength={255} />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-mono text-muted-foreground">Password *</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input type={showPassword ? "text" : "password"} placeholder="Min 6 characters" value={form.password} onChange={(e) => update("password", e.target.value)} className="pl-10 pr-10 font-mono text-sm bg-card border-border" required minLength={6} />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-mono text-muted-foreground">Phone</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input type="tel" placeholder="(555) 123-4567" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="pl-10 font-mono text-sm bg-card border-border" maxLength={20} />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-mono text-muted-foreground">Address</label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="123 Main St" value={form.address} onChange={(e) => update("address", e.target.value)} className="pl-10 font-mono text-sm bg-card border-border" maxLength={200} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <Input placeholder="City" value={form.city} onChange={(e) => update("city", e.target.value)} className="font-mono text-sm bg-card border-border" maxLength={100} />
            <Input placeholder="State" value={form.state} onChange={(e) => update("state", e.target.value)} className="font-mono text-sm bg-card border-border" maxLength={2} />
            <Input placeholder="ZIP" value={form.zip} onChange={(e) => update("zip", e.target.value)} className="font-mono text-sm bg-card border-border" maxLength={10} />
          </div>

          <Button type="submit" className="w-full font-mono" disabled={loading}>
            {loading ? "Creating account..." : "CREATE ACCOUNT"}
          </Button>
        </form>

        <p className="text-center text-xs font-mono text-muted-foreground">
          Already have an account?{" "}
          <Link to="/signin" className="text-primary hover:underline">Sign in</Link>
        </p>
      </div>
      <BottomNav />
    </div>
  );
};

export default SignUp;
